import { useState, type ReactNode } from 'react';
import { Draggable } from '@carbon/react/icons';
import {
  DndContext,
  DragOverlay,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { CSS } from '@dnd-kit/utilities';
import classNames from 'classnames';
import styles from './sortable-list.module.scss';

// Stable module-level array so DndContext doesn't see a new `modifiers` ref each render.
const DND_MODIFIERS = [restrictToVerticalAxis];

export interface SortableListProps<Item> {
  items: Item[];
  getItemKey: (item: Item) => string;
  renderItem: (item: Item, index: number) => ReactNode;
  onReorder: (items: Item[]) => void;
}

// One draggable row. Only the grip carries the drag listeners, so buttons or
// inputs inside a row stay clickable. Each row shows a top divider; while it is
// being dragged the row collapses to a dashed placeholder and the real card
// floats in the DragOverlay.
function SortableRow({ id, children }: { id: string; children: ReactNode }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });
  return (
    <div
      ref={setNodeRef}
      className={classNames(styles.item, { [styles.placeholder]: isDragging })}
      style={{ transform: CSS.Transform.toString(transform), transition }}
    >
      <button
        ref={setActivatorNodeRef}
        type="button"
        className={styles.grip}
        aria-label="Drag to reorder"
        {...attributes}
        {...listeners}
      >
        <Draggable />
      </button>
      <div className={styles.content}>{children}</div>
    </div>
  );
}

/**
 * A list whose rows are dragged into a new order by a grip handle. Uses dnd-kit
 * for a smooth transform-based drag: the picked-up row floats as a lifted card
 * (DragOverlay) while its slot shows a dashed placeholder. Only the handle starts
 * a drag. Callers draw each row via renderItem and get the reordered list via
 * onReorder.
 * @param {SortableListProps<Item>} props - The component props
 * @return {JSX.Element} The sortable list
 */
export const SortableList = <Item,>({
  items,
  getItemKey,
  renderItem,
  onReorder,
}: SortableListProps<Item>): JSX.Element => {
  const [activeId, setActiveId] = useState<string | null>(null);
  const sensors = useSensors(
    // Small activation distance so a click on a row's controls doesn't start a drag.
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );
  const ids = items.map(getItemKey);
  const activeIndex = activeId ? ids.indexOf(activeId) : -1;
  const activeItem = activeIndex >= 0 ? items[activeIndex] : null;

  const handleDragEnd = (e: DragEndEvent) => {
    setActiveId(null);
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const from = ids.indexOf(String(active.id));
    const to = ids.indexOf(String(over.id));
    if (from === -1 || to === -1) return;
    onReorder(arrayMove(items, from, to));
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      modifiers={DND_MODIFIERS}
      onDragStart={(e: DragStartEvent) => setActiveId(String(e.active.id))}
      onDragEnd={handleDragEnd}
      onDragCancel={() => setActiveId(null)}
    >
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        <div className={styles.list}>
          {items.map((item, i) => (
            <SortableRow key={getItemKey(item)} id={getItemKey(item)}>
              {renderItem(item, i)}
            </SortableRow>
          ))}
        </div>
      </SortableContext>
      <DragOverlay modifiers={DND_MODIFIERS}>
        {activeItem ? (
          <div className={classNames(styles.item, styles.overlay)}>
            <span className={styles.grip}>
              <Draggable />
            </span>
            <div className={styles.content}>
              {renderItem(activeItem, activeIndex)}
            </div>
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};
